/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { computed, markRaw, ref } from 'vue';
import { useMutation } from '@pinia/colada';
import ClerkAiService from '@/services/insights/ClerkAiService';
import { clampPanelWidth, toSummary, toTable } from '@/utils/clerkAiHelpers';
import {
  CLERK_AI_DEFAULT_WIDTH,
  CLERK_AI_PREFS_KEY,
} from '@/constants/clerkAiConstants';
import type {
  ClerkAiAssistantMessage,
  ClerkAiMessage,
  ClerkAiPanelPrefs,
} from '@/models/clerkAiTypes';

function readPrefs(): ClerkAiPanelPrefs {
  const defaults = { width: CLERK_AI_DEFAULT_WIDTH };
  try {
    const saved = JSON.parse(localStorage.getItem(CLERK_AI_PREFS_KEY) || '{}');
    return {
      width: Number.isFinite(saved.width) ? saved.width : defaults.width,
    };
  } catch {
    return defaults;
  }
}

function writePrefs(prefs: ClerkAiPanelPrefs) {
  try {
    localStorage.setItem(CLERK_AI_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Storage can be full or blocked; the panel still works for this page load.
  }
}

/**
 * One chat shared by every ClerkAI entry point (header, reports dashboard); the Vue islands share a
 * Pinia singleton. Closing the panel keeps the chat; only newChat() clears it.
 *
 * newChat() bumps `chatToken` so an answer still in flight for the old chat is dropped, not written
 * into the new one.
 *
 * The panel width is a UI preference, kept in localStorage across page loads; expanded (full
 * screen) lasts for the page load only.
 */
export const useClerkAiStore = defineStore('clerkAi', () => {
  const isOpen = ref(false);
  const messages = ref<ClerkAiMessage[]>([]);
  const sending = ref(false);
  const prefs = readPrefs();
  const width = ref(prefs.width);
  const expanded = ref(false);

  const isEmpty = computed(() => messages.value.length === 0);

  let nextId = 1;
  let chatToken = 0;

  const askMutation = useMutation({
    mutation: ({ query, context }: { query: string; context: string }) =>
      ClerkAiService.ask(query, context),
  });

  function open() {
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
  }

  function toggle() {
    isOpen.value = !isOpen.value;
  }

  /** Called on every pointer move during a drag, so it doesn't write to storage; see persistWidth. */
  function setWidth(px: number, viewportWidth: number) {
    width.value = clampPanelWidth(px, viewportWidth);
  }

  function persistWidth() {
    writePrefs({ width: width.value });
  }

  function toggleExpanded() {
    expanded.value = !expanded.value;
  }

  function newChat() {
    chatToken += 1;
    messages.value = [];
    sending.value = false;
  }

  function currentContext() {
    return (window.location.hash || '').replace(/^#/, '');
  }

  async function answer(reply: ClerkAiAssistantMessage) {
    const token = chatToken;
    sending.value = true;
    try {
      const response = await askMutation.mutateAsync({
        query: reply.question,
        context: currentContext(),
      });
      if (token !== chatToken) return;
      reply.summary = toSummary(response ?? {});
      const table = toTable(response ?? {});
      reply.table = table ? markRaw(table) : null;
      reply.status = 'done';
    } catch (e) {
      if (token !== chatToken) return;
      reply.status = 'error';
      throw e;
    } finally {
      if (token === chatToken) sending.value = false;
    }
  }

  async function send(text: string) {
    const question = text.trim();
    if (!question || sending.value) return;

    messages.value.push({ id: nextId++, role: 'user', text: question });
    messages.value.push({
      id: nextId++,
      role: 'assistant',
      status: 'pending',
      question,
      summary: '',
      table: null,
    });
    // Mutate through the reactive proxy, not the plain object pushed above.
    await answer(
      messages.value[messages.value.length - 1] as ClerkAiAssistantMessage,
    );
  }

  async function retry(id: number) {
    const reply = messages.value.find((m) => m.id === id);
    if (
      !reply ||
      reply.role !== 'assistant' ||
      reply.status !== 'error' ||
      sending.value
    )
      return;
    reply.status = 'pending';
    await answer(reply);
  }

  return {
    isOpen,
    messages,
    sending,
    isEmpty,
    width,
    expanded,
    open,
    close,
    toggle,
    setWidth,
    persistWidth,
    toggleExpanded,
    newChat,
    send,
    retry,
  };
});
